import type { Resource } from "@p0security/psql-grants";
import {
  AccessRequestOf,
  type RequestOf,
  isAccessRequestOf,
} from "@p0security/shared/access/types";
import {
  newDirectoryGroupAccesses,
  newDirectoryGroupAndRoleAccesses,
} from "@p0security/shared/integrations/directories/accesses";
import { AwsRdsAccesses } from "@p0security/shared/integrations/resources/aws-rds/accesses";
import { AwsAccesses } from "@p0security/shared/integrations/resources/aws/accesses";
import { getArns } from "@p0security/shared/integrations/resources/aws/util";
import { AzureAccesses } from "@p0security/shared/integrations/resources/azure/accesses";
import { toResourceName } from "@p0security/shared/integrations/resources/azure/util";
import { CsaAccesses } from "@p0security/shared/integrations/resources/csa/accesses";
import { CustomIntegrationAccesses } from "@p0security/shared/integrations/resources/custom/accesses";
import { DbAccesses } from "@p0security/shared/integrations/resources/db/accesses";
import { GcloudAccesses } from "@p0security/shared/integrations/resources/gcloud/accesses";
import { GcloudResourcePermission } from "@p0security/shared/integrations/resources/gcloud/types";
import { GithubAccesses } from "@p0security/shared/integrations/resources/github/accesses";
import { KubernetesAccesses } from "@p0security/shared/integrations/resources/kubernetes/accesses";
import { resourceToFriendlyName } from "@p0security/shared/integrations/resources/kubernetes/display";
import { McpAccesses } from "@p0security/shared/integrations/resources/mcp/accesses";
import { OracleAccesses } from "@p0security/shared/integrations/resources/oracle/accesses";
import { AzureRdpAccesses } from "@p0security/shared/integrations/resources/rdp/accesses/azure";
import { RdpAccesses } from "@p0security/shared/integrations/resources/rdp/accesses/index";
import { SelfHostedAccesses } from "@p0security/shared/integrations/resources/self-hosted/accesses";
import { SnowflakeAccesses } from "@p0security/shared/integrations/resources/snowflake/accesses";
import type {
  KnownProviderSshSessionRequest,
  SshAccesses,
} from "@p0security/shared/integrations/resources/ssh/accesses/index";
import { UnknownSshAccesses } from "@p0security/shared/integrations/resources/ssh/accesses/unknown";
import { TailscaleAccesses } from "@p0security/shared/integrations/resources/tailscale/accesses";
import { WindowsAccesses } from "@p0security/shared/integrations/resources/windows/accesses";
import { ZpaAccesses } from "@p0security/shared/integrations/resources/zpa/accesses";
import { Evidence } from "@p0security/shared/types/evidence";
import { assertNever, assertNeverError } from "@p0security/shared/types/index";
import { isa } from "@p0security/shared/types/is";
import type {
  EmptyAccess,
  EmptyAccesses,
} from "@p0security/shared/types/lifecycle";
import {
  Permission,
  PermissionRequest,
  PermissionType,
  RequestNotificationUnion,
  RequestNotifications,
  isPermissionType,
} from "@p0security/shared/types/permission";
import {
  HandledRequestStatuses,
  RequestStatus,
  RequestStatuses,
  TerminalRequestStatuses,
} from "@p0security/shared/types/request-status";
import { integrationLabels } from "@p0security/shared/types/workflow/constants";
import { widetype } from "@p0security/shared/util/collections";
import { message } from "antd";
import {
  cloneDeep,
  compact,
  get,
  isArray,
  lowerFirst,
  startCase,
} from "lodash";
import concat from "lodash/concat";
import pluralize from "pluralize";
import { FirestoreDoc } from "providers/FirestoreProvider";
import { colors } from "styles/variables";

import {
  directories,
  resourceIntegrations,
} from "../../Integrations/constants.tsx";
import { Integrations } from "./contexts/IntegrationsContext";
import { renderPreApproval } from "./requestRenderUtils";

const SLACK_TLD = "slack.com";
const MICROSOFT_TLD = "microsoft.com";

export const VALID_MESSAGE_TLD = [SLACK_TLD, MICROSOFT_TLD];

const isDictionary = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && !!value;

const noNotifiersConfiguredMessage = (notificationType: string) =>
  `${notificationType} notification not sent, no notifiers configured`;

export const COLOR_MAP: {
  [k: string]: { bg: string; text: string };
} = {
  "Pending Approval": colors.tagColors.blue,
  "Request Created": colors.tagColors.blue,
  Approved: colors.tagColors.blue,
  Staged: colors.tagColors.blue,
  Denied: colors.tagColors.orange,
  Errored: colors.tagColors.red,
  Expired: colors.tagColors.grey,
  Expiring: colors.tagColors.grey,
  Granted: colors.tagColors.blue,
  "N/A": colors.tagColors.grey,
  Relinquished: colors.tagColors.grey,
  Relinquishing: colors.tagColors.grey,
  "Timed Out": colors.tagColors.orange,
} as const;

const BETA_GROUP_ACCESSES = newDirectoryGroupAccesses("beta");

export const ACCESSES_MAP: Readonly<Record<PermissionType, EmptyAccesses>> = {
  "aws-rds": AwsRdsAccesses,
  "azure-ad": newDirectoryGroupAccesses("ga"),
  "cisco-secure-access": CsaAccesses,
  "entra-id": newDirectoryGroupAndRoleAccesses("ga"),
  "self-hosted": SelfHostedAccesses,
  "splunk-stack": {},
  "zscaler-private-access": ZpaAccesses,
  aws: AwsAccesses,
  azure: AzureAccesses,
  custom: CustomIntegrationAccesses,
  gcloud: GcloudAccesses,
  github: GithubAccesses,
  k8s: KubernetesAccesses,
  mcp: McpAccesses,
  mysql: DbAccesses,
  oci: OracleAccesses,
  okta: newDirectoryGroupAccesses("ga"),
  pg: DbAccesses,
  rdp: AzureRdpAccesses,
  snowflake: SnowflakeAccesses,
  ssh: UnknownSshAccesses,
  tailscale: TailscaleAccesses,
  windows: WindowsAccesses,
  workspace: BETA_GROUP_ACCESSES,
};

const logNeverError = (value: never) => console.warn(assertNeverError(value));

export const requestLocator = (
  request: Permission,
  integrations: Integrations
) => {
  const type = request.type as PermissionType;
  switch (type) {
    case "aws-rds":
    case "azure-ad":
    case "azure":
    case "cisco-secure-access":
    case "custom":
    case "entra-id":
    case "gcloud":
    case "github":
    case "k8s":
    case "mcp":
    case "mysql":
    case "oci":
    case "okta":
    case "pg":
    case "rdp":
    case "self-hosted":
    case "snowflake":
    case "tailscale":
    case "windows":
    case "workspace":
    case "zscaler-private-access":
      return isAccessRequestOf(type, request)
        ? accessLocator(ACCESSES_MAP[type], request)
        : undefined;
    // TODO: Derive remaining locators from accesses
    case "aws": {
      const accountId = String(request.permission.accountId);
      return (
        integrations?.aws?.doc?.data?.["iam-write"]?.[accountId]?.label ??
        accountId
      );
    }
    case "splunk-stack": {
      const stackNode = get(request.permission, "resource.stackNode");
      return typeof stackNode === "string" ? stackNode : undefined;
    }
    case "ssh": {
      switch (request.access) {
        case "all":
          return `Routing Rule | ${
            request.permission.routingRuleName ?? "(Unnamed rule)"
          }`;
        case "parent": {
          if (!request.permission.provider) {
            return "unknown";
          }
          return `${
            request.permission.provider === "aws"
              ? "AWS Account"
              : request.permission.provider === "azure"
                ? "Azure Subscription"
                : request.permission.provider === "gcloud"
                  ? "Google Cloud Project"
                  : request.permission.provider === "self-hosted"
                    ? "Self-hosted Environment"
                    : "Unknown"
          }: ${request.permission.alias ?? request.permission.parent}`;
        }
        case "group":
          if (!request.permission.provider) {
            return "all";
          }
          switch (request.permission.provider) {
            case "aws":
              const parentId = request.permission.parent;
              return (
                integrations.ssh?.doc?.data?.["iam-write"]?.[`aws:${parentId}`]
                  ?.label ?? parentId
              );
            case "gcloud":
            case "self-hosted":
              return request.permission.parent;
            case "azure":
              return request.permission.alias ?? request.permission.parent;
            default:
              console.warn("Unknown provider", request.permission.provider);
              return "Unknown";
          }
        case "session":
          switch (request.permission.provider) {
            case "aws":
              const parentId = request.permission.parent;
              return (
                integrations.ssh?.doc?.data?.["iam-write"]?.[`aws:${parentId}`]
                  ?.label ?? parentId
              );
            case "gcloud":
            case "self-hosted":
              return request.permission.parent;
            case "azure":
              return request.permission.alias ?? request.permission.parent;
            default:
              console.warn("Unknown provider", request.permission.provider);
              return "Unknown";
          }
        default:
          console.warn("Unknown access", request.access);
          return "Unknown";
      }
    }
    default: {
      const _validateExhaustion: never = type;
      console.warn("Unknown resource", type);
    }
  }
};

export const requestDescription = (request: Permission): string => {
  const { permission } = request;
  const type = request.type as PermissionType;
  try {
    switch (type) {
      case "aws-rds":
      case "azure-ad":
      case "cisco-secure-access":
      case "entra-id":
      case "github":
      case "mcp":
      case "mysql":
      case "okta":
      case "pg":
      case "self-hosted":
      case "tailscale":
      case "windows":
      case "workspace":
      case "zscaler-private-access":
        return isAccessRequestOf(type, request)
          ? accessDescription(ACCESSES_MAP[type], request)
          : "Unknown";
      case "aws":
        return isAccessRequestOf<"aws", AwsAccesses>("aws", request)
          ? buildAwsDescription(request)
          : "Unknown";
      case "gcloud":
        return isAccessRequestOf<"gcloud", GcloudAccesses>("gcloud", request)
          ? buildGcloudDescription(request)
          : "Unknown";
      case "snowflake":
        return isAccessRequestOf<"snowflake", SnowflakeAccesses>(
          "snowflake",
          request
        )
          ? buildSnowflakeDescription(request)
          : "Unknown";
      case "custom":
        return buildCustomIntegrationDescription(permission);
      case "splunk-stack":
        return buildSplunkStackDescription(permission);
      case "k8s":
        return isAccessRequestOf<"k8s", KubernetesAccesses>("k8s", request)
          ? buildK8sDescription(request)
          : "Unknown";
      case "ssh":
        return isAccessRequestOf<"ssh", SshAccesses>("ssh", request)
          ? `${buildSshDescription(request)}${buildSudoDescription(
              request.permission
            )}`
          : "Unknown";
      case "rdp":
        return isAccessRequestOf<"rdp", RdpAccesses>("rdp", request)
          ? buildRdpDescription(request)
          : "Unknown";
      case "azure":
        return isAccessRequestOf<"azure", AzureAccesses>("azure", request)
          ? buildAzureIamDescription(request)
          : "Unknown";
      case "oci":
        return buildOracleDescription(request);
      default: {
        const _validateExhaustion: never = type;
        console.warn("Got unexpected permission request", {
          ...(permission as any),
        });
      }
    }
  } catch (error) {
    console.error("Error building request description", {
      request,
      error,
    });
  }
  return "Unknown";
};

export const accessLocator = <Accesses extends EmptyAccesses>(
  accesses: Accesses,
  request: AccessRequestOf<Accesses, keyof Accesses>
): string | undefined => {
  const access = accesses[request.access];
  if (!access || !access.render.locator) return undefined;
  const value = request.permission[access.render.locator];
  if (typeof value !== "string") return undefined;
  return value;
};

const buildOracleDescription = (request: Permission) => {
  return `Group | ${request.permission.groupName}`;
};

const parameterDescription = (
  label: string,
  value: string[] | boolean | object | string
): string | undefined =>
  typeof value === "boolean"
    ? value
      ? label
      : `No ${lowerFirst(label)}`
    : typeof value === "string"
      ? value
      : isArray(value)
        ? value.length === 0
          ? "(none)"
          : value.length === 1
            ? value[0]
            : `${value[0]} + ${value.length - 1} other(s)`
        : undefined;

const accessExtras = <Access extends EmptyAccess>(
  access: Access,
  permission: RequestOf<Access>["permission"]
): string | undefined => {
  const extras = access.render.extra?.map(
    (e) => `${access.parameters[e].label}: ${permission[e]}`
  );
  if (!extras) return undefined;
  return `${extras.join(" | ")}`;
};

export const accessDescription = <Accesses extends EmptyAccesses>(
  accesses: Accesses,
  request: AccessRequestOf<Accesses, keyof Accesses>
) => {
  const access = accesses[request.access];
  if (!access) return "Unknown";
  return compact([
    access.label,
    access.render.policy &&
      parameterDescription(
        access.parameters[access.render.policy].label,
        request.permission[access.render.policy]
      ),
    accessExtras(access, request.permission),
  ]).join(" | ");
};

const buildAzureIamDescription = (spec: AccessRequestOf<AzureAccesses>) => {
  return `Role | ${spec.permission.roleName} on ${toResourceName(spec.permission)}`;
};

const buildSnowflakeDescription = (
  request: AccessRequestOf<SnowflakeAccesses>
) => {
  if (request.access === "sql") {
    return `SQL text | ${buildSnowflakeSqlTextDescription(request.permission)}`;
  } else if (request.access === "role") {
    return `Role | ${request.permission.name}`;
  } else {
    // @ts-expect-error TS is just wrong here
    logNeverError(request.access);
    return "Unknown";
  }
};

const buildCustomIntegrationDescription = (
  permission: Permission["permission"]
) => {
  return `${permission.application} | ${permission.item}`;
};

const buildSplunkStackDescription = (permission: Permission["permission"]) => {
  const { resource } = permission;
  if (!isDictionary(resource)) return "Unknown";
  const stackNode =
    typeof resource.stackNode === "string" ? resource.stackNode : "Unknown";
  const role = typeof resource.role === "string" ? resource.role : "Unknown";
  return `${stackNode} | ${role}`;
};

const buildK8sDescription = ({
  permission,
}: AccessRequestOf<KubernetesAccesses>) => {
  return `Resource | ${permission.role} on ${resourceToFriendlyName(
    permission.resource
  )}`;
};

const buildAwsDescription = (request: AccessRequestOf<AwsAccesses>) => {
  switch (request.access) {
    case "policy":
      return `${
        getArns(request.permission).length > 1 ? "Policies" : "Policy"
      } | ${getArns(request.permission)
        .map((arn) => arn.split("/").slice(-1).join(""))
        .join(
          ", "
        )}${request.permission.extendsName ? `, All policies from ${request.permission.extendsName}` : ""}`;
    case "resource":
      return `Resource | ${(request.permission.policies ?? [])
        .map?.((p) => p.split("/").slice(-1).join(""))
        .join(", ")} on ${request.permission.arn}`;
    case "group":
      return `Group | ${request.permission.name}`;
    case "role":
      return `Role | ${request.permission.name}`;
    case "permission-set":
      return `Permission set | ${request.permission.name}`;
    default:
      // TODO: restore `throw` after migrating old request format
      try {
        assertNever(request);
      } catch (error: any) {
        logNeverError(request);
      }
      return "Unknown AWS access";
  }
};

const buildGcloudDescription = (spec: AccessRequestOf<GcloudAccesses>) => {
  switch (spec.access) {
    case "role":
      const roleName =
        spec.permission.role?.substring(
          spec.permission.role?.indexOf("roles/") + 6
        ) ?? "";
      return `Role | ${roleName}`;
    case "permission":
      return `Permissions | ${spec.permission?.name?.join(", ")}`;
    case "resource": {
      const permissionList = spec.permission
        .permissionList as GcloudResourcePermission[];
      const grant = permissionList?.at(0);
      if (!grant) return "no access";
      return `Resource | ${grant.access.accessName} on ${
        grant.requestedResource?.name ?? "(unknown)"
      }`;
    }
    default:
      logNeverError(spec);
      return "Unknown";
  }
};

const buildSnowflakeSqlTextDescription = (
  sql: AccessRequestOf<SnowflakeAccesses, "sql">["permission"]
) => {
  if (sql.grants === undefined) {
    return "Missing grants";
  }

  let writes = [];
  let reads = [];
  if ("read" in sql.grants || "write" in sql.grants) {
    // This if-branch is for backward-compatibility after change https://github.com/p0-security/main-repo/pull/275
    const grants = sql.grants as any | undefined;
    writes = grants?.write ?? [];
    reads = grants?.read ?? [];
  } else {
    writes = concat(sql.grants.insert, sql.grants.delete, sql.grants.update);
    reads = sql.grants.select;
  }
  const count = writes.length + reads.length;
  let mostSensitive:
    | {
        resource: Resource;
        operations: string;
      }
    | undefined;
  if (writes.length > 0) {
    mostSensitive = {
      resource: writes[0],
      operations: "INSERT,UPDATE,DELETE",
    };
  } else if (reads.length > 0) {
    mostSensitive = {
      resource: reads[0],
      operations: "SELECT",
    };
  }
  if (mostSensitive !== undefined) {
    let desc = `${mostSensitive.operations} / ${mostSensitive.resource.catalog}.${mostSensitive.resource.schema}.${mostSensitive.resource.relation}`;
    if (count > 1) {
      desc += ` and ${count - 1} more`;
    }
    return desc;
  }
  return "Missing grants";
};

const buildSshDescription = (spec: AccessRequestOf<SshAccesses>) => {
  switch (spec.access) {
    case "all":
      return `Routing Rule | ${
        spec.permission.routingRuleName ?? "(Unnamed rule)"
      }`;
    case "parent":
      return "All Instances";
    case "session":
      return "resource" in spec.permission
        ? `Instance | ${buildSshSessionDescription(spec.permission)}`
        : "Unknown";
    case "group":
      return `Group | ${spec.permission.name}`;
    default:
      logNeverError(spec);
      return "Unknown";
  }
};

const buildSshSessionDescription = (
  spec: KnownProviderSshSessionRequest["permission"]
) => {
  switch (spec.provider) {
    case "aws":
      return spec.resource.instanceName || spec.resource.instanceId;
    case "gcloud":
      return spec.resource.instanceName || spec.resource.fullName;
    case "azure":
      return spec.resource.instanceName || spec.resource.instanceId;
    case "self-hosted":
      return spec.resource.hostname || spec.resource.hostId;
    default:
      logNeverError(spec);
      return "Unknown";
  }
};

const buildSudoDescription = (spec: Permission["permission"]) => {
  return spec.sudo ? " with sudo" : "";
};

const buildRdpDescription = (spec: AccessRequestOf<RdpAccesses>) => {
  if (spec.access === "session") {
    return `Instance | ${spec.permission.destination}`;
  }
  return "Unknown";
};

const friendlyPrincipal = (request: PermissionRequest) =>
  request.type === "snowflake"
    ? (request.permission.user ?? request.principal)
    : request.principal;

export type RequestLog = {
  initialStatus: RequestStatus;
  updates?: PermissionRequest;
};

export const progressMessages: Record<RequestStatus, string> = {
  NEW: "Setting up workflow",
  PENDING_APPROVAL: "Workflow initiated; waiting for approval",
  PENDING_APPROVAL_ESCALATED:
    "Workflow initiated with escalated notification; waiting for approval",
  APPROVED: "Approved; notifying requestor",
  APPROVED_NOTIFIED: "Staging access",
  ERRORED: "Errored; notifying requestor",
  ERRORED_NOTIFIED: "N/A",
  REVOKE_SUBMITTED: "Revoking request",
  DENIED: "Denied; notifying requestor",
  DENIED_NOTIFIED: "N/A",
  DONE: "Access granted; notifying requestor",
  DONE_NOTIFIED: "Access granted; awaiting expiry",
  DRAFT: "N/A",
  EXPIRY_SUBMITTED: "Expiring request",
  EXPIRED: "Access expired; notifying requestor",
  EXPIRED_NOTIFIED: "N/A",
  REVOKED: "Revoking request; notifying requestor",
  REVOKED_NOTIFIED: "N/A",
  TRANSLATED: "N/A",
  ERRORED_ERRORED: "N/A",
  STAGED: "Granting access",
  TIMED_OUT: "Timed out waiting for approval",
  TIMED_OUT_NOTIFIED: "N/A",
  CLEANUP_SUBMITTED: "Cleanup in progress",
  CLEANED_UP: "Cleanup completed",
  CLEANUP_ERRORED: "Cleanup encountered an error",
};

export const extractApproverName = (
  request: PermissionRequest,
  richPreApproval: boolean
) => {
  // check approvalDetails first, then approverName, then slack notification
  // in approvalDetails
  const { approvalDetails: details, notifications } = request;
  return details
    ? details.approvalSource === "persistent"
      ? "Persistent access"
      : details.approvalSource === "evidence"
        ? renderPreApproval(getPreApprovalData(request), richPreApproval)
        : (details.email ?? details.name ?? details.id ?? "(Unknown approver)")
    : // Old Request, will be migrated to approvalDetails
      ((request as any).approverName ??
        (notifications?.slack as any)?.approvalByEmail ??
        (request.status === "NEW" || request.status === "PENDING_APPROVAL"
          ? "N/A"
          : "Unknown Approver"));
};

export const statusDescriptions: Record<
  RequestStatus,
  (request: PermissionRequest) => string | undefined
> = {
  EXPIRED: (request) => `
        Access expired; ${requestDescription(request)} removed from${" "}
        ${friendlyPrincipal(request)}
     `,
  DRAFT: () => "Not yet created",
  NEW: () => "Created request",
  PENDING_APPROVAL: (request) => `
        Sent request for approval to ${renderNotificationApprovalLinks(request)}
      `,
  PENDING_APPROVAL_ESCALATED: (request) => `
        Escalated request for approval
        ${extractEscalationDetails(request) ?? ""}
      `,
  // APPROVED and DENIED must use richOutput = false as statusDescriptions is used both in the browser and in the export
  // files, where they will render as "[object Object]" if richOutput is true. Unfortunately, this means that the
  // timeline view in a request's details will show the plain text as well instead of the rich evidence link. A link
  // will be displayed in the tabular view in a request's details though, as well as in the request history table.
  APPROVED: (request) =>
    `Request approved by ${extractApproverName(request, false)}`,
  DENIED: (request) =>
    `Request denied by ${extractApproverName(request, false)}`,
  REVOKED: (request) => `Access revoked; ${requestDescription(
    request
  )} removed from${" "}
        ${friendlyPrincipal(request)}`,
  STAGED: () => `Staged request`,
  DONE: (request) => `Granted ${requestDescription(request)} to
        ${friendlyPrincipal(request)}`,
  APPROVED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this request was approved`
      : noNotifiersConfiguredMessage("Approval"),
  DENIED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this request was denied`
      : noNotifiersConfiguredMessage("Deny"),
  EXPIRED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this access has expired`
      : noNotifiersConfiguredMessage("Expiration"),
  ERRORED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this request encountered an error`
      : noNotifiersConfiguredMessage("Error"),
  REVOKED_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this access was revoked`
      : noNotifiersConfiguredMessage("Revoke"),
  DONE_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this access was granted`
      : noNotifiersConfiguredMessage("Grant"),
  ERRORED_ERRORED: () => `
        Encountered an irrecoverable error while processing another error;
        contact support@p0.dev for assistance
      `,
  ERRORED: (request) => `
        Encountered an error while trying to process this access request:
        ${request.error?.message}
      `,
  EXPIRY_SUBMITTED: () =>
    "Triggered access expiration (principal still has access at this time)",
  REVOKE_SUBMITTED: (request) => `
        ${request.principal} relinquished ${requestDescription(request)}
      `,
  TRANSLATED: () => "N/A",
  TIMED_OUT: () => "Timed out waiting for approval",
  TIMED_OUT_NOTIFIED: (request) =>
    request.notifications
      ? `Notified ${request.requestor} that this request timed out`
      : noNotifiersConfiguredMessage("Timeout"),
  CLEANUP_SUBMITTED: () => "Cleaning up grants that should not be present",
  CLEANED_UP: () => "Cleanup completed",
  CLEANUP_ERRORED: () => "Cleanup encountered an error",
};

type ApprovalLink = { url?: string; label: string };

export const getNotificationApprovalLinks = (
  notifications?: RequestNotifications
): ApprovalLink[] => {
  if (!notifications) return [];

  const links = widetype.entries(notifications).map(([key, value]) =>
    getNotificationApprovalLink({
      key,
      value,
    } as RequestNotificationUnion)
  );
  return compact(links);
};

const getNotificationApprovalLink = (
  input: RequestNotificationUnion
): ApprovalLink | undefined => {
  if (!input || !input.key || !input.value) return undefined;
  switch (input.key) {
    case "slack":
      return input.value.approvalConversationUrl
        ? { url: input.value.approvalConversationUrl, label: "Slack" }
        : undefined;
    case "msTeams":
      return input.value.approvalConversationUrl
        ? { url: input.value.approvalConversationUrl, label: "Microsoft Teams" }
        : undefined;
    case "email":
      return input.value.approvalEmailData ? { label: "Email" } : undefined;
    case "webex":
      // webex doesn't support links in notifications, so we return the label without a url
      return { label: "Webex" };
    default:
      assertNeverError(input);
      return undefined;
  }
};

const renderNotificationApprovalLinks = (request: PermissionRequest) =>
  getNotificationApprovalLinks(request.notifications)
    .map((link) => link.label)
    .join(", ");

// Helper function for reconstructing request history. Given a status and list of
// request log documents, returns the log for the next logged status, and adds any intermediate
// events that occurred between the two logs
const findNextLog = (
  updates: PermissionRequest,
  logDocs: FirestoreDoc<PermissionRequest>[]
): {
  nextLog: FirestoreDoc<PermissionRequest> | undefined;
  additionalEvents: RequestLog[];
} => {
  if (isa(TerminalRequestStatuses, updates.status)) {
    // TODO: simplify more for handling cleanup statuses
    // the current logic is a bit convoluted
    // with cleanup statuses, the current terminal status can be both terminal
    // and non-terminal statuses based on their zombie grants being present
    // so we make sure the history does not contain any cleanup statuses
    // if there are clean up statuses, we continue to the next clean up status
    const isCleanedUp = logDocs.some((doc) =>
      ["CLEANUP_SUBMITTED", "CLEANED_UP", "CLEANUP_ERRORED"].includes(doc.id)
    );
    let nextLog, additionalEvents;

    if (isCleanedUp) {
      nextLog =
        updates.status !== "CLEANED_UP" && updates.status !== "CLEANUP_ERRORED"
          ? logDocs.find((doc) => doc.id === "CLEANUP_SUBMITTED")
          : undefined;
      additionalEvents =
        updates.status === "CLEANED_UP" || updates.status === "CLEANUP_ERRORED"
          ? [{ initialStatus: updates.status, updates }]
          : [];
    } else {
      nextLog = undefined;
      additionalEvents = [{ initialStatus: updates.status, updates }];
    }

    return { nextLog, additionalEvents };
  }

  if (isa(HandledRequestStatuses, updates.status))
    return {
      nextLog: logDocs.find((doc) => doc.id === updates.status),
      additionalEvents: [],
    };

  // TODO: Refactor to make request updates in a driver class in order to log all request updates
  // Currently, logging is done in the state machine handler, so we do not logs for transitions that
  // occur outside of that. This is a hack to deal with that for now.
  switch (updates.status) {
    case "PENDING_APPROVAL": {
      /**
       * In normal flow pending approval is never recorded as a handled request status, as there will be no logs for it
       * we try to find if the request has been approved or denied. if these state exists, we skip over to that status
       * else we consider it as pending_approval and show pending approval state. but with pending approval escalated state
       * pending_approval state can exist. if the request is escalated, there will be a pending_approval status, which we can
       * use to show users that the request has been escalated and pending_approval_escalated state behaves like pending_approval state
       * and moves to approved or denied state
       */
      const escalated = logDocs.find((doc) => doc.id === "PENDING_APPROVAL");
      if (escalated) {
        return {
          nextLog: escalated,
          additionalEvents: [],
        };
      }
      const nextLog = logDocs.find(
        (doc) =>
          doc.id === "APPROVED" || doc.id === "DENIED" || doc.id === "TIMED_OUT"
      );
      return {
        nextLog,
        additionalEvents: nextLog
          ? []
          : [{ initialStatus: "PENDING_APPROVAL", updates }],
      };
    }
    case "PENDING_APPROVAL_ESCALATED": {
      const nextLog = logDocs.find(
        (doc) => doc.id === "APPROVED" || doc.id === "DENIED"
      );
      return {
        nextLog,
        additionalEvents: [
          { initialStatus: "PENDING_APPROVAL_ESCALATED", updates },
        ],
      };
    }
    case "DONE_NOTIFIED":
      return {
        nextLog: logDocs.find(
          (doc) =>
            doc.id === "EXPIRY_SUBMITTED" || doc.id === "REVOKE_SUBMITTED"
        ),
        additionalEvents: [{ initialStatus: "DONE_NOTIFIED", updates }],
      };
    default:
      return { nextLog: undefined, additionalEvents: [] };
  }
};

/**
 * @param request - the permission request document
 * @param requestLogs - the collection of logs attached to request. In general, we expect
 * each log to be named after a given status (e.g. "APPROVED_NOTIFIED") and have an 'initialStatus'
 * field that has the name of the next log in the state-path (e.g. "APPROVED").
 * @returns a boolean indicating if the timeline is complete, and a list of RequestLogs
 * that represents a timeline for the permission-request.
 */
export const getRequestEvents = (
  request: PermissionRequest,
  requestLogs: FirestoreDoc<PermissionRequest>[]
): { events: RequestLog[]; isComplete: boolean } => {
  const requestEvents: RequestLog[] = [];

  // Assume that a "NEW" log exists for each permission request; timeline always starts from here.
  let currentLog = requestLogs.find((doc) => doc.id === "NEW");
  let updates = currentLog?.data;

  while (currentLog) {
    if (isa(RequestStatuses, currentLog.id)) {
      requestEvents.push({
        initialStatus: currentLog.id,
        updates,
      });
    }

    // The updates variable contains details that allow findNextLog to infer events that
    // aren't represented by a separate logs document, but rather by data within
    // a given logs document (e.g. notification details)
    updates = currentLog.data;
    const { nextLog, additionalEvents } = findNextLog(updates, requestLogs);
    requestEvents.push(...additionalEvents);
    currentLog = updates.status != currentLog.id ? nextLog : undefined;
  }

  // Infer if we should mark the timeline as 'complete' or render a spinner
  let isComplete: boolean =
    requestEvents.length > 0 &&
    TerminalRequestStatuses.includes(
      requestEvents[requestEvents.length - 1].initialStatus
    );

  // As a safeguard, we include a sanity check: if the permission request itself seems
  // to have terminated, but the last item of the timeline doesn't seem to, then
  // we mark the timeline as complete and tack on the actual final status.
  if (!isComplete && isa(TerminalRequestStatuses, request.status)) {
    isComplete = true;
    requestEvents.push({
      initialStatus: request.status,
      updates: request, // include the request so that we have an accurate timestamp
    });
  }

  return {
    events: requestEvents,
    isComplete,
  };
};

function extractEscalationDetails(request: PermissionRequest) {
  const { escalationDetails } = request;
  if (escalationDetails) {
    if (escalationDetails.pagerduty) {
      const incidents = escalationDetails.pagerduty;
      return `and created pagerduty ${pluralize(
        "incident",
        incidents?.length
      )} ${incidents.join(", ")}`;
    }

    if (escalationDetails.incidentio) {
      const incidents = escalationDetails.incidentio;
      return `and created incidentio ${pluralize(
        "incident",
        incidents?.length
      )} ${incidents.join(", ")}`;
    }
  }
  return undefined;
}

export const statusToDisplayText = (status: string) => {
  const rs = status as RequestStatus;
  switch (rs) {
    case "APPROVED":
    case "APPROVED_NOTIFIED":
      return "Approved";
    case "CLEANED_UP":
      return "Cleanup Completed";
    case "CLEANUP_ERRORED":
      return "Errored";
    case "CLEANUP_SUBMITTED":
      return "Cleanup in Progress";
    case "EXPIRY_SUBMITTED":
      return "Expiring";
    case "DENIED":
    case "DENIED_NOTIFIED":
      return "Denied";
    case "DONE":
    case "DONE_NOTIFIED":
      return "Granted";
    case "ERRORED":
    case "ERRORED_ERRORED":
    case "ERRORED_NOTIFIED":
      return "Errored";
    case "EXPIRED":
    case "EXPIRED_NOTIFIED":
      return "Expired";
    case "NEW":
      return "Request Created";
    case "PENDING_APPROVAL":
    case "PENDING_APPROVAL_ESCALATED":
      return "Pending Approval";
    case "REVOKE_SUBMITTED":
      return "Relinquishing";
    case "REVOKED":
    case "REVOKED_NOTIFIED":
      return "Relinquished";
    case "STAGED":
      return "Staged";
    case "DRAFT":
    case "TRANSLATED":
      return "N/A";
    case "TIMED_OUT":
    case "TIMED_OUT_NOTIFIED":
      return "Timed Out";
    case undefined:
    default: {
      const _validateExhaustion: never = rs;
      return "Unknown";
    }
  }
};

export const deserializeQueryParamValue = (value: string | null) => {
  if (value === "null") {
    return null;
  } else if (value === "true") {
    return true;
  } else if (value === "false") {
    return false;
  } else if (!Number.isNaN(Number(value))) {
    return Number(value);
  } else if (!!value) {
    return value;
  } else {
    return null;
  }
};

export const serializeQueryParamValue = (
  value: boolean | number | string | null
): string | null => {
  if (value === null) {
    return null;
  } else if (value === true) {
    return "true";
  } else if (value === false) {
    return "false";
  } else if (typeof value === "number") {
    if (Number.isNaN(value)) {
      return null;
    }
    return value.toString();
  } else if (!!value) {
    return value;
  }
  return null;
};

export const getPreApprovalData = (
  request: PermissionRequest
): readonly [Evidence["source"], string] | "unknown" | undefined => {
  const { approvalDetails } = request;
  const { approvalSource } = approvalDetails ?? {};

  if (approvalSource !== "evidence") {
    return undefined;
  }

  const { evidence: evidenceList } = request;

  if (!evidenceList || evidenceList.length === 0) {
    return "unknown";
  }

  const [evidence] = evidenceList;
  const { source } = evidence;

  switch (source) {
    case "request":
      return ["request", evidence.requestId] as const;
    case "allow":
      return ["allow", evidence.allowId] as const;
    default:
      assertNever(source);
  }
};

export const handleRequestActionResponse = (
  response: Response | undefined,
  success: string
) => {
  if (response?.ok) {
    message.success(success);
  }
};

export const toRequestDescription = (request: PermissionRequest) =>
  requestDescription(request)?.replace(/.*\| /, "");

export const toRequestType = ({ type }: PermissionRequest) =>
  isPermissionType(type) ? integrationLabels[type] : "Unknown";

export const toRequestLocation = (
  request: PermissionRequest,
  integrations: Integrations,
  flags: Record<string, boolean>
) => {
  const location = requestLocator(request, integrations);
  const integrationMap = [
    ...cloneDeep(resourceIntegrations(flags)),
    ...cloneDeep(directories),
  ];

  return location
    ? String(location)
    : integrationMap.find((i) => i.key === request.type)?.label;
};
export const toRequestAccessType = (request: PermissionRequest) =>
  "access" in request ? startCase(request.access) : "";
